import Env from '@/config/Env'
import Button from '@/shared/Button'
import FormItem from '@/shared/FormCreator/FormItem'
import { yupResolver } from '@hookform/resolvers/yup'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'

import debounce from '@mui/material/utils/debounce'
import isEmpty from 'lodash/isEmpty'
import unionBy from 'lodash/unionBy'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { connect, useSelector } from 'react-redux'
import * as Yup from 'yup'

import EditEmailTemplateLabels from '@/components/EditEmailTemplateLabels'
import EmailDialog from '@/components/EmailDialog'
import TextEditor from '@/shared/TextEditor'

const mapStateToProps = ({ loading }) => ({
  isLandingPageLoading: loading['landingpage/getAddableLandingPages'],
})

const inputConfig = {
  variant: 'standard',
  inputBaseProps: {
    labelSuffix: '',
    flexDirection: 'column',
    alignItems: 'stretch',
    spacing: 0,
  },
  endAdornment: args => {
    const { value, inputProps } = args

    const { maxLength } = inputProps || {}

    if (maxLength) {
      return (
        <Typography
          variant="caption"
          sx={{ color: '#00000040', fontSize: '14px !important' }}
        >{`${value.length}/${maxLength}`}</Typography>
      )
    }
    return null
  },
}

const placeholderOptions = [
  { text: 'Recipient full name', value: 'recipient_full_name' },
  { text: 'Recipient chinese name', value: 'recipient_cname' },
  { text: 'Recipient department', value: 'recipient_department' },
  { text: 'Recipient email', value: 'recipient_email' },
  { text: 'Recipient company', value: 'recipient_company' },
  { text: 'Recipient company logo', value: 'recipient_company_logo' },
  { text: 'My first name', value: 'my_first_name' },
  { text: 'My last name', value: 'my_last_name' },
  { text: 'My email', value: 'my_email' },
  { text: 'My email domain', value: 'my_email_domain' },
  { text: 'Campaign start date', value: 'campaign_start_date' },
  { text: 'Campaign start date year', value: 'campaign_start_date_year' },
  { text: 'Campaign start date month', value: 'campaign_start_date_month' },
  { text: 'Campaign start date month fullname', value: 'campaign_start_date_month_fullname' },
  { text: 'Campaign start date day', value: 'campaign_start_date_day' },
  { text: 'Campaign start date time', value: 'campaign_start_date_time' },
  { text: 'Current Year', value: 'current_year' },
]

const PhishEmailTemplateForm = ({
  dispatch,
  data,
  onSubmit: propsOnSubmit,
  isLandingPageLoading,
  type,
}) => {
  const { t } = useTranslation()

  const isSystem = type === 'system'

  const [preview, setPreview] = useState({})
  const [restoreContent, setRestoreContent] = useState('')
  const [showLabels, setShowLabels] = useState(false)
  const [selectedLabels, setSelectedLabels] = useState([])
  const [editorError, setEditorError] = useState()

  const companyId = useSelector(state => state.auth.account?.companyId)

  Yup.addMethod(Yup.object, 'landingPageSelect', function testLandingPageSelect(errorMessage) {
    return this.test('test-landing-page-select', errorMessage, (_, context) => {
      const { parent = {}, path, createError } = context
      const { isLandingPage, landingPage } = parent

      if (isLandingPage && (landingPage == null || !landingPage.id)) {
        return createError({ path, message: errorMessage })
      }
      return true
    })
  })

  Yup.addMethod(Yup.string, 'attachmentName', function testAttachmentName(errorMessage) {
    return this.test('test-attachment-name', errorMessage, (_, context) => {
      const { parent = {}, path, createError } = context

      const { isAttachment, attachmentName } = parent

      if (isAttachment && isEmpty(attachmentName)) {
        return createError({ path, message: errorMessage })
      }

      return true
    })
  })

  Yup.addMethod(Yup.string, 'attachmentExtension', function testAttachmentExtension(errorMessage) {
    return this.test('test-attachment-extension', errorMessage, (_, context) => {
      const { parent = {}, path, createError } = context

      const { isAttachment, extension } = parent

      if (isAttachment && isEmpty(extension)) {
        return createError({ path, message: errorMessage })
      }
      return true
    })
  })

  Yup.addMethod(
    Yup.object,
    'landingPageAttachment',
    function testLandingPageAttachment(errorMessage) {
      return this.test('test-landingPageAttachment', errorMessage, (_, context) => {
        const { parent = {}, path, createError } = context

        const { extension } = parent
        const { isAttachment, landingPageAttachment } = parent
        if (
          isAttachment &&
          isEmpty(landingPageAttachment) &&
          (extension === 'html' || extension === 'htm')
        ) {
          return createError({ path, message: errorMessage })
        }
        return true
      })
    },
  )

  Yup.addMethod(Yup.string, 'attachmentContent', function testAttachmentContent(errorMessage) {
    return this.test('test-attachmentContent', errorMessage, (_, context) => {
      const { parent = {}, path, createError } = context

      const { extension } = parent
      const { isAttachment, attachmentContent } = parent
      if (
        isAttachment &&
        isEmpty(attachmentContent) &&
        (extension === 'excel' || extension === 'word')
      ) {
        return createError({ path, message: errorMessage })
      }
      return true
    })
  })

  // const searchInputRef = useRef()
  // const searchInputRefAttachement = useRef()
  const textEditorRef = useRef()

  const [searchInput, setSearchInput] = useState('')
  const [searchResults, setSearchResults] = useState({})
  const [searchResultsAttachements, setSearchResultsAttachements] = useState({})
  const [page, setPage] = useState(0)
  const [pageAttachement, setPageAttachement] = useState(0)

  const [forceLandingPage, setForceLandingPage] = useState(false)

  const { data: searchResultsData = [], paging } = searchResults
  const { data: searchResultsDataAttachement = [], pagingAttachement } = searchResultsAttachements

  const fetchData = useCallback(() => {
    dispatch({
      type: 'landingPage/getAddableLandingPages',
      payload: {
        pageIndex: page,
        pageSize: 100,
        searchName: searchInput,
        type: isSystem ? 'S' : 'I',
      },
      successCallback: response => {
        const { data: responseData, paging: responsePaging } = response
        const { pageIndex } = responsePaging

        const newData = responseData.map(d => ({ id: d.landingpageTempId, label: d.name }))

        setSearchResults(s => ({
          data: pageIndex > 0 ? unionBy(s.data || [], newData, 'id') : newData,
          paging: responsePaging,
        }))
      },
    })
  }, [dispatch, isSystem, page, searchInput])

  const loadMoreDebounce = debounce(() => {
    setPage(p => p + 1)
  }, 400)

  const fetchDataDebounce = useMemo(
    () =>
      debounce(() => {
        fetchData()
      }, 400),
    [fetchData],
  )

  useEffect(() => {
    fetchDataDebounce()
  }, [searchInput, page, fetchDataDebounce])

  const fetchDataAttachement = useCallback(() => {
    dispatch({
      type: 'landingPage/getAddableLandingPages',
      payload: {
        pageIndex: pageAttachement,
        pageSize: 100,
        searchName: searchInput,
        type: isSystem ? 'S' : 'I',
      },
      successCallback: response => {
        const { data: responseDataAttachement, paging: responsePaging } = response
        const { pageIndex } = responsePaging

        const newDataAttachement = responseDataAttachement.map(d => ({
          id: d.landingpageTempId,
          label: d.name,
        }))

        setSearchResultsAttachements(s => ({
          data:
            pageIndex > 0 ? unionBy(s.data || [], newDataAttachement, 'id') : newDataAttachement,
          pagingAttachement: responsePaging,
        }))
      },
    })
  }, [dispatch, isSystem, pageAttachement, searchInput])

  const loadMoreAttachementDebounce = debounce(() => {
    setPageAttachement(p => p + 1)
  }, 400)

  const fetchDataAttachementDebounce = useMemo(
    () =>
      debounce(() => {
        fetchDataAttachement()
      }, 400),
    [fetchDataAttachement],
  )

  useEffect(() => {
    fetchDataAttachementDebounce()
  }, [searchInput, pageAttachement, fetchDataAttachementDebounce])

  let validations = {
    name: Yup.string().required(t('validation.template_name_required')),
    // labels: Yup.string().required(t('validation.label_required')),
    senderName: Yup.string().required(t('validation.sender_name_required')),
    subject: Yup.string().required(t('validation.html_title_required')),
    senderEmail: Yup.string()
      .required(t('validation.send_email_required'))
      .email(t('validation.email_format')),
    landingPage: Yup.object()
      .landingPageSelect(t('validation.landing_page_required'))
      .typeError(t('validation.landing_page_required'))
      .nullable(),
    attachmentName: Yup.string()
      .matches(/^[^!@#$%^&*`~'";:<>,./?|\\]*$/, t('validation.invalid_filename'))
      .attachmentName(t('validation.attachment_name_required'))
      .nullable(),
    extension: Yup.string()
      .attachmentExtension(t('validation.attachment_extension_required'))
      .nullable(),
    landingPageAttachment: Yup.object()
      .landingPageAttachment(t('validation.attachment_landingPage_required'))
      .nullable(),
    attachmentContent: Yup.string()
      .attachmentContent(t('validation.attachment_attachmentContent_required'))
      .nullable(),
  }

  if (isSystem) {
    validations = {
      ...validations,
      difficulty: Yup.string().required(t('validation.difficulty_required')),
    }
  }

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    trigger,
    // eslint-disable-next-line no-unused-vars
    formState: { errors },
    reset: formReset,
  } = useForm({
    resolver: yupResolver(Yup.object().shape(validations)),
  })

  useEffect(() => {
    if (data) {
      const {
        isAttachement: formIsAttachment,
        attachementName: formAttachmentName,
        isRefLandingpage: formIsLandingPage,
        extension: fromExtension,
        attachmentContent,
        landingPageAttachmentId,
        landingPageAttachmentName,
        refLandingpage: formLandingPage,
        name: formName,
        difficulty: formDifficulty,
        senderName: formSenderName,
        senderAddress: formSenderAddress,
        subject: formSubject,
        content: formContent,
        labelList: formLabelList,
        refLandingpageName,
      } = data

      if (formIsAttachment != null) {
        setValue('isAttachment', formIsAttachment)
        if (formAttachmentName !== null) {
          setValue('attachmentName', formAttachmentName)
        }
        if (fromExtension !== null) {
          setValue('extension', fromExtension)
        }
        if (fromExtension === 'html' || fromExtension === 'htm') {
          if (landingPageAttachmentId && landingPageAttachmentName) {
            setValue('landingPageAttachment', {
              id: landingPageAttachmentId,
              label: landingPageAttachmentName,
            })
          } else {
            setValue('landingPageAttachment', { id: '', label: '' })
          }
        }
        if (fromExtension === 'docm' || fromExtension === 'xlsm') {
          if (fromExtension === 'docm') {
            setValue('extension', 'word')
          } else {
            setValue('extension', 'excel')
          }
          setValue('attachmentContent', attachmentContent)
        } else {
          setValue('attachmentContent', '')
        }
      }

      if (formIsLandingPage !== null) {
        setValue('isLandingPage', formIsLandingPage)
        if (formLandingPage && refLandingpageName) {
          setValue('landingPage', { id: formLandingPage, label: refLandingpageName })
        } else {
          setValue('landingPage', { id: '', label: '' })
        }
      }

      if (formName !== null) {
        setValue('name', formName)
      }

      if (formDifficulty !== null) {
        setValue('difficulty', formDifficulty)
      }

      if (formSenderName !== null) {
        setValue('senderName', formSenderName)
      }

      if (formSenderAddress !== null) {
        setValue('senderEmail', formSenderAddress)
      }

      if (formSubject !== null) {
        setValue('subject', formSubject)
      }

      if (formContent !== null) {
        setRestoreContent(formContent)
      }

      if (formLabelList !== null) {
        setSelectedLabels(formLabelList || [])
      }

      const hasLandingPage = formContent?.includes('{{landing_page_url}}')
      const hasQrcodeShow = formContent?.includes(Env.QRCODE_URL)

      if (hasLandingPage || hasQrcodeShow) {
        setValue('isLandingPage', true)
        setForceLandingPage(true)
      }
    }
  }, [data, setValue])

  useEffect(() => {
    if (selectedLabels) {
      setValue('labels', selectedLabels.map(({ label }) => label).join(','))
    }
    trigger?.('labels')
  }, [setValue, selectedLabels, trigger])

  useEffect(() => {
    setEditorError('')
  }, [])

  // eslint-disable-next-line no-unused-vars
  const checkQrcodeAndLandingPage = (editorContents, landingPages) => {
    if (editorContents !== null) {
      if (editorContents.includes(Env.QRCODE_URL)) {
        if (landingPages.id === '' && landingPages.label === '') {
          // if(!editorContents.includes('{{landing_page_url}}')){
          return false
        }
      }
    }
    return true
  }

  const onSubmit = values => {
    const {
      name,
      difficulty,
      subject,
      senderName,
      senderEmail,
      isLandingPage: isRefLandingpage,
      landingPage,
      isAttachment,
      attachmentName,
      extension,
      attachmentContent,
      landingPageAttachment,
    } = values

    // 信件內容
    const editorContent = textEditorRef.current.editor().getContent()

    // 有 URL (不管QRCODE)
    const hasLandingPage = editorContent?.includes('{{landing_page_url}}')

    // NEW 有 QRCODE (不管URL)
    const hasQrcodeShow = editorContent?.includes(Env.QRCODE_URL)
    // console.log(editorContent, hasLandingPage, hasQrcodeShow)

    // 有 QRCODE 時 要有 LandingPage
    // const hasQrcodeAndLandingPage = checkQrcodeAndLandingPage(editorContent, landingPage)

    // isRefLandingpage = 有 LandingPage ; NEW !hasQrcodeShow
    const linkMissing = isRefLandingpage && !(hasLandingPage || hasQrcodeShow)

    const hasWordAndExcel = extension === 'word' || extension === 'excel'

    // const linkMissing = !hasLandingPage && !hasWordAndExcel

    if (linkMissing) {
      if (hasLandingPage && !hasWordAndExcel) {
        setEditorError(t('validation.landing_page_url_required'))
      } else if (hasWordAndExcel && !hasLandingPage) {
        setEditorError(t('validation.attachement_word_or_excel_required'))
      }
      setEditorError(t('validation.landing_page_url_required'))
    }

    // if (!hasQrcodeAndLandingPage) {
    //   setEditorError(t('validation.qrcode_must_has_landingPage'))
    // }

    // hasQrcodeAndLandingPage
    if (!linkMissing) {
      const payload = {
        name,
        labelList: selectedLabels.map(({ label }) => label),
        subject,
        senderName,
        senderAddress: senderEmail,
        attachementName: attachmentName,
        extension,
        attachmentContent,
        landingPageAttachment,
        content: textEditorRef.current.editor().getContent(),
        gcpStorageUrlList: textEditorRef.current.urlList(),
        type: isSystem ? 'SYSTEM' : 'INDIVIDUAL',
        ...(isEmpty(difficulty) ? {} : { difficulty }),
      }

      if (isRefLandingpage) {
        payload.refLandingpage = landingPage.id
      } else {
        payload.refLandingpage = null
      }

      if (isAttachment) {
        payload.attachementName = attachmentName
        payload.extension = extension
        if (payload.extension === 'word' || payload.extension === 'excel') {
          payload.landingPageAttachment = null
          payload.attachmentContent = attachmentContent
        } else if (payload.extension === 'html' || payload.extension === 'htm') {
          payload.attachmentContent = null
          payload.landingPageAttachment = landingPageAttachment.id
        }
      } else {
        payload.attachementName = null
        payload.extension = null
        payload.attachmentContent = null
        payload.landingPageAttachment = null
      }

      propsOnSubmit?.(payload)
    }

    formReset?.({}, { keepValues: true })
  }

  const isLandingPage = watch('isLandingPage')
  const landingPage = watch('landingPage')
  const isAttachment = watch('isAttachment')
  const attachmentType = watch('extension')
  const landingPageAttachment = watch('landingPageAttachment')

  useEffect(() => {
    if (isLandingPage === false) {
      setValue('landingPage', null)
    }
  }, [setValue, isLandingPage])

  return (
    <Container maxWidth={false}>
      <Card>
        <CardContent>
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit, () => {
              trigger?.()
            })}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormItem
                  label={t('general.template_name')}
                  defaultValue=""
                  name="name"
                  control={control}
                  inputConfig={{ ...inputConfig, inputProps: { maxLength: 80 } }}
                  errors={errors}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormItem
                  label={t('general.label')}
                  defaultValue=""
                  name="labels"
                  control={control}
                  inputConfig={{
                    ...inputConfig,
                    InputProps: { readOnly: true },
                    onClick: () => {
                      setShowLabels(true)
                    },
                  }}
                  errors={errors}
                />
              </Grid>

              {isSystem && (
                <Grid item xs={12} sm={6}>
                  <FormItem
                    label={t('general.difficulty')}
                    defaultValue=""
                    name="difficulty"
                    type="select"
                    control={control}
                    inputConfig={{
                      ...inputConfig,
                      options: [
                        { value: 'EASY', label: t('general.easy') },
                        { value: 'MEDIUM', label: t('general.normal') },
                        { value: 'HARD', label: t('general.hard') },
                      ],
                    }}
                    errors={errors}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <FormItem
                  label={t('general.mail_subject')}
                  defaultValue=""
                  name="subject"
                  control={control}
                  inputConfig={{ ...inputConfig, inputProps: { maxLength: 100 } }}
                  errors={errors}
                />
              </Grid>

              {!isSystem && <Grid item xs={0} sm={6} />}

              <Grid item xs={12} sm={6}>
                <FormItem
                  label={t('general.sender')}
                  defaultValue=""
                  name="senderName"
                  control={control}
                  inputConfig={{ ...inputConfig, inputProps: { maxLength: 40 } }}
                  errors={errors}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormItem
                  label={t('general.sender_mail')}
                  defaultValue=""
                  name="senderEmail"
                  control={control}
                  inputConfig={{ ...inputConfig, inputProps: { maxLength: 80 } }}
                  errors={errors}
                />
              </Grid>

              <Grid item xs={12}>
                <TextEditor
                  ref={textEditorRef}
                  error={editorError}
                  isQRcode={true}
                  onInit={editor => {
                    setTimeout(() => {
                      if (data?.content) {
                        editor.setContent(data.content)
                      }
                    }, 0)
                  }}
                  onContentChange={content => {
                    // 偵測 URL
                    const hasLandingPage = content?.includes('{{landing_page_url}}')
                    // setForceLandingPage(hasLandingPage)
                    // NEW 偵測 QRcode
                    const hasQrcodeShow = content?.includes(Env.QRCODE_URL)

                    // 小改邏輯
                    if (hasLandingPage || hasQrcodeShow) {
                      setValue('isLandingPage', true)
                      setForceLandingPage(true)
                    } else {
                      setValue('isLandingPage', false)
                      setForceLandingPage(false)
                    }
                  }}
                  restoreContent={restoreContent}
                  placeholderOptions={placeholderOptions}
                  imageUploadCallback={(file, cb, errCb) => {
                    const formData = new FormData()
                    formData.append('file', file)

                    dispatch({
                      type: 'emailTemplate/uploadEmailTemplateImage',
                      payload: formData,
                      successCallback: res => {
                        cb(res)
                      },
                      errorCallback: err => {
                        errCb(err)
                      },
                    })
                  }}
                />
              </Grid>

              <Grid container item xs={12}>
                <Grid item>
                  <FormItem
                    label={t('general.select_landing_page')}
                    defaultValue={false}
                    name="isLandingPage"
                    disabled={forceLandingPage}
                    type="checkbox"
                    control={control}
                    inputConfig={{
                      ...inputConfig,
                    }}
                    errors={errors}
                  />
                </Grid>

                {isLandingPage && (
                  <Grid item>
                    <Stack direction="row" spacing={2}>
                      <FormItem
                        name="landingPage"
                        type="autocomplete"
                        control={control}
                        errors={errors}
                        inputConfig={{
                          ...inputConfig,
                          sx: { width: 200, mt: 1 },
                          onInputChange: (_, value, reason) => {
                            // searchInputRef.current = value

                            if (['input', 'clear'].includes(reason)) {
                              setPage(0)
                              setSearchInput(value)
                            }
                          },
                          onFocus: () => {
                            fetchDataDebounce()
                          },
                          options: searchResultsData,
                          filterOptions: opts => opts,
                          isOptionEqualToValue: (option, value) => option.id === value.id,
                          renderOption: (renderProps, option) => (
                            <Box component="li" {...renderProps} key={option.id}>
                              {option.label}
                            </Box>
                          ),
                          ListboxProps: {
                            role: 'list-box',
                            sx: { maxHeight: 200 },
                            onScroll: event => {
                              const listboxNode = event.currentTarget

                              if (
                                listboxNode.scrollTop + listboxNode.clientHeight >=
                                listboxNode.scrollHeight - 10
                              ) {
                                const { hasNext } = paging

                                if (!isLandingPageLoading && hasNext) {
                                  loadMoreDebounce()
                                }
                              }
                            },
                          },
                        }}
                      />
                      <Button
                        disabled={!landingPage}
                        onClick={() => {
                          dispatch({
                            type: 'landingPage/getLandingPageHtml',
                            payload: { landingpageTempId: landingPage.id },
                            successCallback: html => {
                              const w = window.open('', '_blank', '') // access the "about:blank" window you've opened
                              w.document.body.innerHTML = html
                            },
                          })
                        }}
                      >
                        {t('general.preview')}
                      </Button>
                    </Stack>
                  </Grid>
                )}
              </Grid>

              <Grid item xs={12}>
                <FormItem
                  label={t('general.add_attachment')}
                  defaultValue={false}
                  name="isAttachment"
                  type="checkbox"
                  control={control}
                  inputConfig={{
                    ...inputConfig,
                  }}
                  errors={errors}
                />

                {isAttachment && (
                  <Box ml={3}>
                    <FormItem
                      label={`*${t('general.attachment_name')}`}
                      defaultValue=""
                      name="attachmentName"
                      control={control}
                      inputConfig={{
                        ...inputConfig,
                        inputProps: { maxLength: 40 },
                        inputBaseProps: {},
                      }}
                      errors={errors}
                    />

                    <FormItem
                      label={`*${t('general.select_extension')}`}
                      defaultValue=""
                      name="extension"
                      type="select"
                      control={control}
                      inputConfig={{
                        ...inputConfig,
                        inputBaseProps: { alignItems: 'center' },
                        options: [
                          { value: 'html', label: 'html' },
                          { value: 'htm', label: 'htm' },
                          { value: 'word', label: 'word' },
                          { value: 'excel', label: 'excel' },
                        ],
                      }}
                      errors={errors}
                    />
                    {(attachmentType === 'html' || attachmentType === 'htm') && (
                      <Stack direction="row" spacing={2}>
                        <FormItem
                          label={t('general.select_landing_page')}
                          name="landingPageAttachment"
                          type="autocomplete"
                          control={control}
                          errors={errors}
                          inputConfig={{
                            ...inputConfig,
                            inputBaseProps: { alignItems: 'center' },
                            sx: { width: 200, mt: 1 },
                            onInputChange: (_, value, reason) => {
                              // searchInputRefAttachement.current = value

                              if (['input', 'clear'].includes(reason)) {
                                setPage(0)
                                setSearchInput(value)
                              }
                            },
                            onFocus: () => {
                              fetchDataAttachementDebounce()
                            },
                            options: searchResultsDataAttachement,
                            filterOptions: opts => opts,
                            isOptionEqualToValue: (option, value) => option.id === value.id,
                            renderOption: (renderProps, option) => (
                              <Box component="li" {...renderProps} key={option.id}>
                                {option.label}
                              </Box>
                            ),
                            ListboxProps: {
                              role: 'list-box',
                              sx: { maxHeight: 200 },
                              onScroll: event => {
                                const listboxNode = event.currentTarget

                                if (
                                  listboxNode.scrollTop + listboxNode.clientHeight >=
                                  listboxNode.scrollHeight - 10
                                ) {
                                  const { hasNext } = pagingAttachement
                                  if (!isLandingPageLoading && hasNext) {
                                    loadMoreAttachementDebounce()
                                  }
                                }
                              },
                            },
                          }}
                          // errors={errors}
                        />
                        <Button
                          disabled={!landingPageAttachment}
                          onClick={() => {
                            dispatch({
                              type: 'landingPage/getLandingPageHtml',
                              payload: { landingpageTempId: landingPageAttachment.id },
                              successCallback: html => {
                                const w = window.open('', '_blank', '') // access the "about:blank" window you've opened
                                w.document.body.innerHTML = html
                              },
                            })
                          }}
                        >
                          {t('general.preview')}
                        </Button>
                      </Stack>
                    )}

                    {(attachmentType === 'word' || attachmentType === 'excel') && (
                      <FormItem
                        label={`${t('general.attachment_content')}`}
                        defaultValue=""
                        type="text"
                        name="attachmentContent"
                        control={control}
                        inputConfig={{
                          ...inputConfig,
                          inputProps: { maxLength: 40 },
                          inputBaseProps: {},
                        }}
                        errors={errors}
                      />
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>

            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item>
                <Button
                  onClick={() => {
                    const { name, senderEmail, senderName, subject } = getValues() || {}
                    dispatch({
                      type: 'common/generatePreviewHtml',
                      payload: {
                        content: textEditorRef.current.editor().getContent(),
                        landingpageId: landingPage?.id,
                        type: 'E',
                      },
                      successCallback: ({ content }) => {
                        const parser = new DOMParser()
                        const doc = parser.parseFromString(content, 'text/html')

                        const anchors = doc.getElementsByTagName('a')

                        for (let i = 0; i < anchors.length; i += 1) {
                          anchors[i].setAttribute('target', '_blank')
                        }

                        setPreview({
                          details: {
                            emailContent: doc.documentElement.innerHTML,
                            emailSubject: subject,
                            senderName,
                            snederAddress: senderEmail,
                          },
                          title: name,
                        })
                      },
                    })
                  }}
                >
                  {t('general.preview')}
                </Button>
              </Grid>

              {(companyId === 1 || !isSystem) && (
                <Grid item>
                  <Button type="submit">{t('general.complete')}</Button>
                </Grid>
              )}
            </Grid>
          </Box>
        </CardContent>
      </Card>

      <EmailDialog
        open={!!preview.details}
        details={preview.details}
        title={preview.title}
        onClose={() => setPreview({})}
      />

      {showLabels && (
        <EditEmailTemplateLabels
          isEdit={!!data}
          emailTempId={data?.emailTempId}
          open={showLabels}
          data={selectedLabels}
          onClose={() => {
            setShowLabels(false)
          }}
          onComplete={selected => {
            setSelectedLabels(selected)
          }}
          type={type}
        />
      )}
    </Container>
  )
}

export default connect(mapStateToProps)(PhishEmailTemplateForm)
